import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, EMPTY, finalize, map, Observable, take } from "rxjs";
import { StoreService } from "src/app/shared/services/store.service";
import { environment } from "src/environments/environment";
import { GroupTimeLine, IsDisbursedShown } from "./loan-dashboard.interface";
import { GtmService } from "src/app/shared/services/gtm-service/gtm.service";
import { GlobalService } from "src/app/shared/services/global.service";
import { UserPropertiesService } from "src/app/shared/services/user-services/user-properties.service";

@Injectable({
    providedIn: "root",
})
export class LoanDashboardService {
    baseUrl = `${environment.baseUrl}`;
    timelineSubject = new BehaviorSubject<any>([]);
    timeline$ = this.timelineSubject.asObservable();
    loanDashboardSimmer = new BehaviorSubject<Boolean>(false);

    constructor(
        private http: HttpClient,
        private router: Router,
        private storeService: StoreService,
        private gtmService: GtmService,
        private global: GlobalService,
        private userPropertiesService: UserPropertiesService
    ) {
        let token = this.storeService.get("token");
        if (token) {
            this.getCustomerLocationOnceIn24Hours();
        }
    }

    getDashboardData(groupTimeLine: boolean) {
        return this.http.get<any>(`${this.baseUrl}/loan-module/loan/dashboard/data`, {
            params: {
                groupTimeLine: groupTimeLine,
            },
        });
    }

    getCustomerLocation() {
        return this.http.get<any>(`${this.baseUrl}/auth/customer-location`);
    }

    getCustomerLocationOnceIn24Hours() {
        const LAST_CALL_KEY = "customerLocationAPICalledAt";
        const lastCalled = this.storeService.get(LAST_CALL_KEY);
        const now = Date.now();

        let shouldCallAPI = true;
        if (lastCalled) {
            const lastCalledTime = Number(lastCalled);
            // 24 hours = 86400000 ms
            if (now - lastCalledTime < 86400000) {
                shouldCallAPI = false;
            }
        }

        if (shouldCallAPI) {
            this.getCustomerLocation().subscribe({
                next: (res) => {
                    this.storeService.set({
                        key: LAST_CALL_KEY,
                        value: now.toString(),
                    });
                },
                error: (err) => {
                    // Handle error if needed
                },
            });
        }
    }

    applyLoan(dtcConsent = false) {
        return this.http.get<any>(`${this.baseUrl}/v1/loan-module/apply/loan`, {
            params: {
                dtcConsent: dtcConsent,
            },
        });
    }

    getDashboardDetails(isShimmer = true, redirect: boolean = true): void {
        this.getDashboardDetails$(isShimmer, redirect)
            .pipe(take(1))
            .subscribe({
                next: () => {},
                error: () => {},
            });
    }

    loadData = false;
    getDashboardDetails$(isShimmer = true, redirect: boolean = true): Observable<any> {
        if (!this.loadData) {
            this.loadData = true;
            if (isShimmer) {
                this.router.navigate(["/loan/timeline"]);
                this.loanDashboardSimmer.next(true);
            }

            return this.getDashboardData(GroupTimeLine.YES.value).pipe(
                map((res: any) => {
                    if (res?.success) {
                        // ✅ Process timeline response for GTM events to fire appropriate events based on the response
                        this.gtmService.processTimelineResponse(res);

                        const { loanApplicationId, loanId, loanCount, rewardWalletBalance, unreadNotificationsCount } =
                            res?.data || {};

                        if (loanApplicationId) {
                            this.storeService.set({
                                key: "applicationId",
                                value: JSON.stringify(loanApplicationId),
                            });
                        }
                        if (loanId) {
                            this.storeService.set({
                                key: "loanId",
                                value: JSON.stringify(loanId),
                            });
                        }
                        if (loanCount) {
                            this.storeService.set({
                                key: "loanCount",
                                value: JSON.stringify(loanCount),
                            });
                        }

                        let count = unreadNotificationsCount ? unreadNotificationsCount : 0;
                        this.global.unreadNotificationsCount.next(count);

                        let rewardBalance = rewardWalletBalance ? rewardWalletBalance : 0;
                        this.global.rewardWalletBalance.next(rewardBalance);

                        this.timelineSubject.next(res.data);

                        this.userPropertiesService.setDashboardUserProperties(res.data);

                        // here need to data fetch from cookie and send to marketing api
                        let checkMarketingApiCalled = this.storeService.get("marketingAPICalled");
                        let isGeneratedAuthToken = this.storeService.get("isGeneratedAuthToken");
                        if (!checkMarketingApiCalled && isGeneratedAuthToken !== "true") {
                            this.getMarketingDataFromCookie();
                        }

                        if (res?.data?.stage && redirect) {
                            this.handleStage(res?.data);
                        }
                    }

                    if (!redirect) {
                        return res?.data || null;
                    }
                }),
                finalize(() => {
                    this.loadData = false;
                    setTimeout(() => {
                        this.loanDashboardSimmer.next(false);
                    }, 0);
                })
            );
        } else {
            return EMPTY;
        }
    }

    async handleStage(data: any) {
        let stage = data.stage;
        await this.router.navigateByUrl("/", { skipLocationChange: true });
        if (stage === "DTC_PENDING" || stage === "DTC_INPROGRESS") {
            this.router.navigate(["/d2c"]);
        } else if (stage === "ELIGIBLE_FOR_LOAN") {
            this.router.navigate(["/loan/apply"], { state: { data } });
        } else if (stage === "TIMELINE") {
            this.router.navigate(["/loan/timeline"]);
        } else if (stage === "KYC_NEEDED") {
            this.router.navigate(["loan/aadhaar-kyc"]);
        } else if (["PROFILE_PENDING", "ADMIN_VERIFICATION_PENDING"].includes(stage)) {
            this.router.navigate(["/loan/re-take"], { state: { stage } });
        } else if (stage === "REPAYMENT_PENDING") {
            this.redirectToRepayment();
        } else if (["UPLOAD_PAN_CARD", "PAN_VERIFICATION_PENDING"].includes(stage)) {
            this.router.navigate(["/loan/pan-expired"], { state: { stage } });
        } else {
            this.router.navigate(["/loan/criteria-mismatch"]);
        }
    }

    redirectToRepayment() {
        this.timeline$.pipe(take(1)).subscribe((timeline) => {
            if (timeline && Object.keys(timeline).length > 0) {
                if (timeline?.isDisbursedShown == IsDisbursedShown.SHOWN) {
                    this.router.navigate(["loan/re-pay"]);
                } else if (timeline?.isDisbursedShown == IsDisbursedShown.NOT_SHOWN) {
                    this.router.navigate(["/loan/disbursement"]);
                }
            }
        });
    }

    getCustomerProfile() {
        return this.http.get<any>(`${this.baseUrl}/customer-profile/get`);
    }

    getMarketingDataFromCookie() {
        let customerData = this.storeService.get("customerDetails");
        const parsed = typeof customerData === "string" ? JSON.parse(customerData) : customerData;
        let customerId = parsed?.customerId || "";

        let cookieParams = document.cookie.split(";").reduce(
            (acc, cookie) => {
                const [key, ...valParts] = cookie.split("=");
                const value = valParts.join("=");
                acc[key.trim()] = value.trim();
                return acc;
            },
            {} as Record<string, string>
        );

        let clientId = cookieParams["_ga"] || "";
        if (clientId) {
            const parts = clientId.split(".");
            clientId = parts.slice(-2).join(".");
        }

        let session = cookieParams["_ga_VXCJKZ2W06"] || "";
        if (session) {
            const sessionParts = session.split(".");
            session = sessionParts.slice(2).join(".");
            session = session.substring(1).split("$")[0];
        }
        const hashedMobile =
            ((window as any).dataLayer || []).find((d: any) => d.user?.hashed_mobileno)?.user.hashed_mobileno || "";
        this.storeService.set({ key: "marketingAPICalled", value: "1" });
        const keys = [
            "utm_source",
            "utm_medium",
            "utm_campaign",
            "utm_id",
            "utm_term",
            "utm_content",
            "gclid",
            "click_id",
            "gbraid",
            "fbclid",
        ];
        let storedParams: Record<string, string> = {};
        const storedUtmData = this.storeService.get("utm_data");
        if (storedUtmData) {
            try {
                storedParams = JSON.parse(storedUtmData);
            } catch {}
        }
        const utm_data = keys.reduce(
            (acc, key) => {
                acc[key] =
                    storedParams[key] ||
                    this.storeService.get(key) ||
                    (["utm_source", "utm_medium"].includes(key) ? "direct" : "none");
                return acc;
            },
            {} as Record<string, string>
        );
        Object.assign(utm_data, storedParams);
        this.http
            .post<any>(`${this.baseUrl}/marketing/user`, {
                web: {
                    user_id: hashedMobile,
                    session_id: session,
                    client_id: clientId,
                },
                utm_data,
            })
            .subscribe({
                next: (res) => console.log("API success", res),
                error: (err) => console.error("API error", err),
            });
    }
}
